import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, ArrowRightLeft, Eye, Shield } from 'lucide-react';
import { api, formatCurrency, expenseCategories } from '../lib/utils';
import { toast } from 'sonner';

// Full categories list matching BankStatementUpload
const CATEGORY_OPTIONS = [
  { value: 'revenue', label: 'Revenue', group: 'Income' },
  ...expenseCategories,
  { value: 'investment', label: 'Investment', group: 'Finance' },
  { value: 'loan_emi', label: 'Loan EMI', group: 'Finance' },
  { value: 'sip', label: 'SIP', group: 'Finance' },
];

function ConfidenceBadge({ level, label }) {
  const colors = {
    high: 'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-red-100 text-red-800 border-red-200',
  };
  const pct = Math.round(level * 100);
  const tier = pct >= 90 ? 'high' : pct >= 60 ? 'medium' : 'low';
  return (
    <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${colors[tier]}`}>
      {label}: {pct}%
    </span>
  );
}

function AuditTrail({ tx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-600">
        <Eye className="w-3 h-3" /> Audit {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-1 p-2 rounded bg-zinc-50 border text-[11px] space-y-1">
          <div><span className="text-zinc-500">Raw text:</span> <span className="font-mono">{tx.raw_text}</span></div>
          <div><span className="text-zinc-500">Parser:</span> {tx.parser_used}</div>
          <div><span className="text-zinc-500">Direction method:</span> <span className="font-medium">{tx.direction_method}</span></div>
          <div><span className="text-zinc-500">Direction reason:</span> {tx.direction_reason}</div>
          {tx.rule_used && <div><span className="text-zinc-500">Rule matched:</span> <span className="font-medium text-blue-600">{tx.rule_used}</span></div>}
          <div><span className="text-zinc-500">Category source:</span> {tx.category_source}</div>
          {tx.reference_number && <div><span className="text-zinc-500">Reference:</span> {tx.reference_number}</div>}
          {tx.duplicate_check && (
            <div className="text-amber-600"><span className="text-zinc-500">Duplicate:</span> {tx.duplicate_check.match_type} — {tx.duplicate_check.match_details}</div>
          )}
          <div className="flex gap-3 pt-1 border-t">
            <ConfidenceBadge level={tx.confidence_detail?.extraction || tx.confidence?.extraction || 0} label="Extract" />
            <ConfidenceBadge level={tx.confidence_detail?.direction || tx.confidence?.direction || 0} label="Direction" />
            <ConfidenceBadge level={tx.confidence_detail?.merchant || tx.confidence?.merchant || 0} label="Merchant" />
            <ConfidenceBadge level={tx.confidence_detail?.category || tx.confidence?.category || 0} label="Category" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExceptionReview() {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('exceptions'); // 'exceptions' | 'all'
  const [merchantRules, setMerchantRules] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);

  // Merge all categories
  const allCategories = [...CATEGORY_OPTIONS, ...customCategories.map(c => ({ ...c, group: 'Custom' }))];

  // Load recent batches
  useEffect(() => {
    const loadBatches = async () => {
      try {
        const res = await api.get('/bank-statement/recent-batches');
        setBatches(res.data || []);
        if (res.data?.length > 0) setSelectedBatch(res.data[0].batch_id);
      } catch { /* ignore */ }
    };
    loadBatches();
  }, []);

  // Load merchant rules and custom categories
  useEffect(() => {
    api.get('/merchant-rules').then(res => setMerchantRules(res.data || [])).catch(() => {});
    api.get('/categories/custom').then(res => setCustomCategories(res.data || [])).catch(() => {});
  }, []);

  // Load batch data when selected
  const loadBatch = useCallback(async () => {
    if (!selectedBatch) return;
    setLoading(true);
    try {
      const [excRes, reconRes] = await Promise.all([
        api.get(`/bank-statement/exceptions/${selectedBatch}`),
        api.get(`/bank-statement/reconciliation/${selectedBatch}`).catch(() => ({ data: null }))
      ]);
      setExceptions(excRes.data?.exceptions || []);
      setReconciliation(reconRes.data?.reconciliation || null);

      // Load all transactions for this batch
      const allRes = await api.get(`/bank-statement/batch-transactions/${selectedBatch}`);
      setAllTransactions(allRes.data || []);
    } catch (e) {
      toast.error('Failed to load batch data');
    } finally {
      setLoading(false);
    }
  }, [selectedBatch]);

  useEffect(() => { loadBatch(); }, [loadBatch]);

  const displayTxns = filter === 'exceptions' ? exceptions : allTransactions;

  const handleFlipDirection = async (tx) => {
    const newDir = tx.type === 'credit' ? 'debit' : 'credit';
    const newCat = newDir === 'credit' ? 'revenue' : (tx.category === 'revenue' ? 'other' : tx.category);
    try {
      await api.patch('/bank-statement/update-type', null, {
        params: { batch_id: tx.batch_id, transaction_date: tx.date, description: tx.description, new_type: newDir, new_category: newCat }
      });
      await api.post('/bank-statement/learn-correction', {
        narration: tx.description, direction: newDir, category: newCat
      });
      toast.success(`Corrected to ${newDir} — learned for future imports`);
      loadBatch();
    } catch { toast.error('Failed to update'); }
  };

  const handleCategoryChange = async (tx, newCat) => {
    try {
      await api.patch('/bank-statement/update-category', null, {
        params: { batch_id: tx.batch_id, transaction_date: tx.date, description: tx.description, new_category: newCat }
      });
      await api.post('/bank-statement/learn-correction', {
        narration: tx.description, category: newCat, direction: tx.type
      });
      toast.success(`Category updated — learned for future imports`);
      loadBatch();
    } catch { toast.error('Failed to update category'); }
  };

  const handleConfirmTransaction = async (tx) => {
    try {
      await api.post('/bank-statement/confirm-exception', {
        batch_id: tx.batch_id,
        transaction_date: tx.date,
        description: tx.description
      });
      toast.success('Transaction confirmed');
      loadBatch();
    } catch { toast.error('Failed to confirm transaction'); }
  };

  const handleConfirmAll = async () => {
    if (!selectedBatch) return;
    try {
      const res = await api.post('/bank-statement/confirm-all-exceptions', {
        batch_id: selectedBatch
      });
      toast.success(res.data.message || `Confirmed ${res.data.count || allTransactions.length} transactions`);
      // Reload both batches list and current batch data
      await loadBatches();
      await loadBatch();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to confirm all'); 
    }
  };

  return (
    <div className="space-y-4" data-testid="exception-review-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Exception Review</h2>
          <p className="text-sm text-zinc-500">Review transactions and click "Confirm All" to import them as expenses/revenues.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="exceptions">Exceptions Only ({exceptions.length})</SelectItem>
              <SelectItem value="all">All Transactions ({allTransactions.length})</SelectItem>
            </SelectContent>
          </Select>
          {batches.length > 0 && (
            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue placeholder="Select batch" /></SelectTrigger>
              <SelectContent>
                {batches.map(b => (
                  <SelectItem key={b.batch_id} value={b.batch_id}>
                    {b.filename || b.batch_id} ({b.count} txns)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {displayTxns.length > 0 && (
            <Button onClick={handleConfirmAll} className="h-8 text-xs bg-green-600 hover:bg-green-700" data-testid="confirm-all-btn">
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirm & Import All ({allTransactions.length})
            </Button>
          )}
        </div>
      </div>

      {/* Reconciliation banner */}
      {reconciliation && (
        <div className={`rounded-lg px-4 py-3 border text-sm ${
          reconciliation.status === 'pass' ? 'bg-green-50 border-green-200' :
          reconciliation.status === 'fail' ? 'bg-red-50 border-red-200' :
          'bg-amber-50 border-amber-200'
        }`} data-testid="recon-banner">
          <div className="flex items-center gap-2">
            {reconciliation.status === 'pass' ? <CheckCircle className="w-4 h-4 text-green-600" /> :
             reconciliation.status === 'fail' ? <XCircle className="w-4 h-4 text-red-600" /> :
             <AlertTriangle className="w-4 h-4 text-amber-600" />}
            <span className="font-medium">
              {reconciliation.status === 'pass' ? 'Reconciliation Passed' :
               reconciliation.status === 'fail' ? 'Reconciliation Failed — Review Required' :
               'Reconciliation Needs Review'}
            </span>
          </div>
          <div className="flex gap-4 mt-1 text-xs text-zinc-600">
            {reconciliation.opening_balance != null && <span>Opening: {formatCurrency(reconciliation.opening_balance)}</span>}
            <span>Debits: {formatCurrency(reconciliation.total_debits)}</span>
            <span>Credits: {formatCurrency(reconciliation.total_credits)}</span>
            {reconciliation.actual_closing != null && <span>Closing: {formatCurrency(reconciliation.actual_closing)}</span>}
          </div>
        </div>
      )}

      {/* Merchant rules summary */}
      {merchantRules.length > 0 && (
        <Card className="roch-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><Shield className="w-4 h-4" /> Learned Rules ({merchantRules.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {merchantRules.slice(0, 15).map(r => (
                <span key={r.vendor_key} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  {r.vendor_name || r.vendor_key} → {r.category || r.direction}
                </span>
              ))}
              {merchantRules.length > 15 && <span className="text-[10px] text-zinc-400">+{merchantRules.length - 15} more</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exception transactions table */}
      {loading ? (
        <div className="text-center py-8 text-zinc-400">Loading...</div>
      ) : displayTxns.length === 0 ? (
        <Card className="roch-card">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <p className="text-zinc-600 font-medium">No exceptions to review</p>
            <p className="text-sm text-zinc-400 mt-1">All transactions were auto-classified with high confidence.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {displayTxns.map((tx, i) => {
            const conf = tx.confidence_detail || tx.confidence || {};
            const overallPct = Math.round((conf.overall || 0) * 100);
            const isLowDir = (conf.direction || 0) < 0.75;
            const isLowCat = (conf.category || 0) < 0.75;

            return (
              <Card key={tx.batch_id + '_' + tx.row_index + '_' + i} className={`roch-card ${isLowDir ? 'border-red-200' : isLowCat ? 'border-amber-200' : 'border-zinc-200'}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">{tx.date}</span>
                        <Badge variant={tx.type === 'credit' ? 'default' : 'secondary'} className={`text-[10px] px-1.5 ${tx.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {tx.type === 'credit' ? 'CR' : 'DR'}
                        </Badge>
                        <span className="font-medium text-sm">{formatCurrency(tx.amount)}</span>
                        {isLowDir && <AlertTriangle className="w-3 h-3 text-red-500" title="Low direction confidence" />}
                      </div>
                      <p className="text-sm text-zinc-700 mt-0.5 truncate">{tx.description}</p>

                      {/* Issue indicators */}
                      <div className="flex gap-1.5 mt-1">
                        {isLowDir && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Direction uncertain</span>}
                        {isLowCat && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Category uncertain</span>}
                        {tx.duplicate_check && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">Potential duplicate</span>}
                        <ConfidenceBadge level={conf.overall || 0} label="Overall" />
                      </div>

                      <AuditTrail tx={tx} />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleFlipDirection(tx)} data-testid={`flip-dir-${i}`}>
                        <ArrowRightLeft className="w-3 h-3" /> {tx.type === 'credit' ? 'Mark Debit' : 'Mark Credit'}
                      </Button>
                      <Select value={tx.category} onValueChange={(val) => handleCategoryChange(tx, val)}>
                        <SelectTrigger className="w-[140px] h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {allCategories.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        size="sm" 
                        className="h-7 text-xs bg-green-600 hover:bg-green-700 gap-1" 
                        onClick={() => handleConfirmTransaction(tx)}
                        data-testid={`confirm-tx-${i}`}
                      >
                        <CheckCircle className="w-3 h-3" /> Confirm
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
